import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Upload, Trash2 } from "lucide-react";

const KIND = { purchases: "Αγορές", expenses: "Δαπάνες", items: "Στατιστική αγορών ανά είδος" };

export const ImportTab = () => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const { data: imports = [] } = useQuery({ queryKey: ["erp-imports"], queryFn: async () => (await api.get("/erp/imports")).data });
  const { data: templates = [] } = useQuery({ queryKey: ["erp-templates"], queryFn: async () => (await api.get("/erp/templates")).data });
  const download = async (t) => {
    const res = await api.get(`/erp/templates/${t.kind}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = t.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const up = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/erp/import?commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      if (!d.committed) { setPreview(d); return; }
      toast.success(`${KIND[d.kind]}: εισήχθησαν ${d.rows} γραμμές`);
      setPreview(null); setFile(null); if (ref.current) ref.current.value = "";
      ;["erp-imports", "erp-stats", "erp-prices", "erp-kpis", "erp-price-alerts", "erp-templates"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (d.new_price_alerts) toast.warning(`${d.new_price_alerts} νέες αυξήσεις τιμών πάνω από 10%`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const years = [...new Set(imports.flatMap((i) => i.years || []))].sort((a, b) => b - a);
  const [delKind, setDelKind] = useState("purchases");
  const [delYear, setDelYear] = useState("");
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/erp/imports?kind=${delKind}&year=${delYear}`)).data,
    onSuccess: (d) => {
      toast.success(`Διαγράφηκαν ${d.deleted} γραμμές (${KIND[d.kind]} ${d.year}). Μπορείτε να ανεβάσετε νέο αρχείο.`);
      ;["erp-imports", "erp-stats", "erp-prices", "erp-kpis", "erp-price-alerts", "facts", "fin-pl"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const confirmDelete = () => {
    if (!delYear) return;
    if (window.confirm(`Να διαγραφούν όλα τα δεδομένα «${KIND[delKind]}» για το ${delYear}; Κρατείται αντίγραφο ασφαλείας.`)) del.mutate();
  };
  return (
    <div className="space-y-6" data-testid="bs-import-tab">
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4">
        <div>
          <h2 className="font-medium text-[#45423C] flex items-center gap-2"><Upload className="h-4 w-4 text-[#C86235]" /> Ανέβασμα Excel από SoftOne</h2>
          <p className="text-sm text-[#6F6A60] mt-1">Αναγνωρίζονται αυτόματα: «Αγορές», «Δαπάνες» και «Στατιστική αγορών ανά είδος». Η εισαγωγή αντικαθιστά τα δεδομένα του ίδιου τύπου για τα έτη του αρχείου. Χρησιμοποιείται η καθαρή αξία· τα πιστωτικά αφαιρούνται.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="bs-import-file" ref={ref} type="file" accept=".xlsx" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          <button data-testid="bs-import-preview" disabled={!file || up.isPending} onClick={() => up.mutate(false)}
            className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#45423C] disabled:opacity-50">Προεπισκόπηση</button>
          {preview && (
            <button data-testid="bs-import-commit" disabled={up.isPending} onClick={() => up.mutate(true)}
              className="rounded-xl bg-[#45423C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2F2D29] disabled:opacity-50">Εισαγωγή</button>
          )}
        </div>
        {preview && (
          <div className="rounded-xl border border-[#E3DED4] p-4 text-sm grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="bs-import-summary">
            <div><div className="text-[10px] uppercase text-[#6F6A60]">Τύπος</div>{KIND[preview.kind]}</div>
            <div><div className="text-[10px] uppercase text-[#6F6A60]">Γραμμές</div>{preview.rows}</div>
            <div><div className="text-[10px] uppercase text-[#6F6A60]">Καθαρή αξία</div>{fmtEuro(preview.net_total)}</div>
            <div><div className="text-[10px] uppercase text-[#6F6A60]">Πιστωτικά</div>{preview.credit_notes}</div>
            <div><div className="text-[10px] uppercase text-[#6F6A60]">Έτη</div>{preview.years.join(", ")}</div>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-3" data-testid="bs-delete-card">
        <h2 className="font-medium text-[#45423C] flex items-center gap-2"><Trash2 className="h-4 w-4 text-[#B4412A]" /> Διαγραφή δεδομένων για νέο ανέβασμα</h2>
        <p className="text-xs text-[#6F6A60]">Σβήνει τα δεδομένα ενός τύπου για ένα έτος (κρατείται backup) και ξαναχτίζει P&L / ledger. Μετά ανεβάζετε το νέο αρχείο από πάνω.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="bs-delete-kind" value={delKind} onChange={(e) => setDelKind(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm">
            {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select data-testid="bs-delete-year" value={delYear} onChange={(e) => setDelYear(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm">
            <option value="">Έτος…</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="bs-delete-btn" disabled={!delYear || del.isPending} onClick={confirmDelete}
            className="rounded-xl border border-[#B4412A] text-[#B4412A] px-4 py-2 text-sm font-semibold hover:bg-[#B4412A] hover:text-white disabled:opacity-50">Διαγραφή</button>
        </div>
      </div>
      {templates.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="bs-templates">
          <h2 className="font-medium text-[#45423C]">Templates για τις επόμενες ανανεώσεις</h2>
          <p className="text-xs text-[#6F6A60] mt-1">Το τελευταίο αρχείο κάθε τύπου. Βγάλτε από το SoftOne την ίδια εκτύπωση με τις ίδιες στήλες και ανεβάστε την εδώ.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {templates.map((t) => (
              <button key={t.kind} data-testid={`bs-template-${t.kind}`} onClick={() => download(t)}
                className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:border-[#45423C]">
                {KIND[t.kind]} · {t.filename}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto" data-testid="bs-import-history">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]"><tr><th className="px-3 py-2 text-left">Ημ/νία</th><th className="px-3 text-left">Τύπος</th><th className="px-3 text-left">Αρχείο</th><th className="px-3 text-right">Γραμμές</th><th className="px-3 text-right">Καθαρή αξία</th><th className="px-3 text-left">Από</th></tr></thead>
          <tbody>
            {imports.map((i) => (
              <tr key={i.id} className={`border-t border-[#E3DED4] ${i.action === "delete" ? "text-[#B4412A]" : ""}`}>
                <td className="px-3 py-2">{new Date(i.at).toLocaleString("el-GR")}</td><td className="px-3">{KIND[i.kind]}</td>
                <td className="px-3 truncate max-w-[220px]">{i.file}</td><td className="px-3 text-right tabular">{i.action === "delete" ? `διαγραφή ${-i.rows}` : i.rows}</td>
                <td className="px-3 text-right tabular">{fmtEuro(i.net_total)}</td><td className="px-3">{i.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
